/* global document, window, fetch, setTimeout */
import CopyToClipboard from 'react-copy-to-clipboard';
import {Link} from 'react-router';
import {FLOW_STEP_HOOK} from '../../common/model-api/FlowStepDefType';
import {safeJSONparse} from '../../common/util/safeJSONparse';
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {injectIntl, defineMessages, FormattedMessage, FormattedDate} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import DetailBox from '../fw/DetailBox.react';
import DetailToolBar from '../fw/DetailToolBar.react';
import RefreshButton from '../jobs/RefreshButton.react';
import TableDetail from '../fw/TableDetail.react';
import types from '../../common/model-api/types';
import * as BrowserPermission from '../BrowserPermission';
import * as Permission from '../../common/util/Permission';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import config from '../../common/config';
import RetryInvocationMutation from './RetryInvocationMutation.react';
import CancelInvocationMutation from './CancelInvocationMutation.react';
import ConfirmDialog from '../fw/ConfirmDialog.react';
import FlowInvocationStatus from './FlowInvocationStatus.react';
import LoadingButton from '../fw/LoadingButtonNew.react';
import UserRenderer from '../users/UserRenderer.react';
import ExtendFetchExpirationMutation from '../fetches/ExtendFetchExpirationMutation.react';
import mutationError from '../channel-defs/mutationError';
import SaveAsTestButton from './SaveAsTestButton.react';
import {displayError} from '../app/errorDisplayer';
import Token from '../../../packages/extension/src/background/Token';

import urlMapping from "../app/URLMapping";
const messages = defineMessages({
    title: {id: 'flowInvocationView.title', 'defaultMessage': 'Flow Invocation'},
	retryButton: {id: 'flowInvocation.retryButton', 'defaultMessage': 'Retry'},
	retryDescription: {id: 'flowInvocation.retryDescriptionLonger', 'defaultMessage': 'Do you want to retry invocation? This can make problems if the previous invocation is still in progress.'},
	cancelButton: {id: 'flowInvocation.cancelButton', 'defaultMessage': 'Stop'},
	cancelDescription: {id: 'flowInvocation.cancelDescription', 'defaultMessage': 'Do you want to stop invocation?'},
    downloadLog: {id: 'flowInvocation.downloadLog', 'defaultMessage': 'Download log'},
});

class FlowInvocationView extends React.Component {

    typeName = 'flowInvocations';

    constructor(props) {
        super(props);
        this.state = {
            saveAsTestError: null,
        };
    }

    onSaveAsTestError = (error) => {
        this.setState({ saveAsTestError: error });
    };

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };

    parseFilenameFromContentDisposition(headerValue) {
        if (!headerValue) {
            return null;
        }
        let m = headerValue.match(/filename="([^"]+)"/);
        return m ? m[1] : null;
    }

    downloadLogs = (format = 'log') => {
        let { user } = this.props;
        let { invocation: flowInvocation } = user.organisation.flow;

        let flowInvocationId = flowInvocation.realId || flowInvocation.id;
        if (!flowInvocationId) {
            displayError(new Error("Missing flowInvocation id"));
            return Promise.resolve();
        }

        // Prefer the same-origin API base used by Relay (`<body data-apihost="/api/v1">`).
        // Using config.apiHost/config.GLOBAL_API_URL here can accidentally point to the webpack server
        // and download the app HTML instead of the log.
        let apiHost = (document.getElementsByTagName('body').item(0).dataset.apihost) || '/api/v1';
        let url = apiHost + '/flow-invocations/' + encodeURIComponent(flowInvocationId) + '/download-log?format=' + encodeURIComponent(format);

        try {
            let token = Token.get();
            if (token && !token.startsWith('Bearer ')) {
                token = 'Bearer ' + token;
            }

            if (!token) {
                displayError(new Error("Missing auth token"));
                return Promise.resolve();
            }

            return fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': token,
                }
            }).then((response) => {
                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('text/html')) {
                    throw new Error('Download returned HTML (likely wrong API host / missing proxy to backend).');
                }
                if (!response.ok) {
                    throw new Error('Download failed (' + response.status + ')');
                }
                let filename = this.parseFilenameFromContentDisposition(response.headers.get('content-disposition')) ||
                    ('flow-invocation-' + flowInvocationId + (format === 'jsonl' ? '.jsonl' : '.log'));
                return response.blob().then((blob) => ({blob, filename}));
            }).then(({blob, filename}) => {
                let blobUrl = window.URL.createObjectURL(blob);
                let a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
            }).catch((e) => {
                displayError(e);
            });
        } catch (e) {
            displayError(e);
            return Promise.resolve();
        }
    };

    onClickExtendExpiration = () => {
        return new Promise((resolve, reject) => {
            let onSuccess = () => resolve();

            this.props.relay.commitUpdate(
                new ExtendFetchExpirationMutation({
                    flowInvocation: this.props.user.organisation.flow.invocation,
                }), {onSuccess: onSuccess, onFailure: (e) => {
                    mutationError(e);
                    reject(e);
                }}
            );
        });
    };



    getStats(flowInvocation) {
        if (!flowInvocation.stats) {
            return null;
        }
        let stats = safeJSONparse(flowInvocation.stats);
        return (<div>
            <h3><FormattedMessage defaultMessage="Network statistics" id="flow.flowInvocationStatistics" /></h3>

            <table>{Object.keys(stats).map((statKey, idx) => {
                if (statKey === "CACHE") {
                    return null;
                }
                return (
                    <tr key={idx}>
                        <th>{statKey}:</th>
                        <td className="r">{stats[statKey]}</td>
                    </tr>
                );
            })}</table>
        </div>);
    }

    retryInvocation() {
        let { user } = this.props;
        let { invocation: flowInvocation } = user.organisation.flow;

		return new Promise((resolve, reject) => {
			this.props.relay.commitUpdate(
				new RetryInvocationMutation({
					flowInvocation: flowInvocation,
				}), {onSuccess: resolve, onFailure: reject}
			);
		});
	}

    onClickRetry() {
		return new Promise((resolve) => {
			let confirmDialogConfig  = {
				message: this.props.intl.formatMessage(messages.retryDescription),
				confirm: this.props.intl.formatMessage(messages.retryButton),
				confirmStyle: 'btn-danger'
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
				return this.retryInvocation().then(() => {
					return resolve();
				});
			}).catch(() => {
				resolve();
			});
		});
	}

    cancelInvocation() {
        let { user } = this.props;
        let { invocation: flowInvocation } = user.organisation.flow;

		return new Promise((resolve, reject) => {
			this.props.relay.commitUpdate(
				new CancelInvocationMutation({
					flowInvocation: flowInvocation,
				}), {onSuccess: resolve, onFailure: reject}
			);
		});
	}

    onClickCancel() {
		return new Promise((resolve) => {
			let confirmDialogConfig  = {
				message: this.props.intl.formatMessage(messages.cancelDescription),
				confirm: this.props.intl.formatMessage(messages.cancelButton),
				confirmStyle: 'btn-danger'
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
				return this.cancelInvocation().then(() => {
					return resolve();
				});
			}).catch(() => {
				resolve();
			});
		});
	}

    constructCURL(flowInvocation) {
        let output = "curl -L -k";

        let originalRequest = flowInvocation.originalRequest2Preview || flowInvocation.originalRequest;

        let hasRequest = originalRequest;

        output += ' -X POST';
        output += ' "' + flowInvocation.originalUrl + '"';

        if (hasRequest) {
            output += ' --data-raw "' + originalRequest.replace(/"/gm, "\\\"") + '"';
        }

        return output;
    }

    render() {
        let { user } = this.props;
        let flow = user.organisation.flow;
        let { invocation: flowInvocation } = user.organisation.flow;

        const isDeveloper = BrowserPermission.hasPermission(Permission.PERM_DEVELOPER) && BrowserPermission.developerModeEnabled();
        const canCaptureSnapshotTesting = BrowserPermission.canCaptureSnapshotTesting(this.props.user.organisation.realId);
        const hasLogs = Boolean(flowInvocation && flowInvocation.logger && flowInvocation.logger.edges && flowInvocation.logger.edges.length > 0);

        let type = types[this.typeName];

        let originalHeaders = "";
        if (flowInvocation.originalHeaders) {
            if (typeof flowInvocation.originalHeaders === 'string') {
                try {
                    originalHeaders = JSON.parse(flowInvocation.originalHeaders);
                } catch (e) {
                }
            }

            if (typeof originalHeaders !== 'string') {
                originalHeaders = (
                    <table><tbody>{Object.keys(originalHeaders).map((key) => {
                        return <tr key={key}><th>{key}:</th><td>{String(originalHeaders[key])}</td></tr>;
                    })}</tbody></table>
                );
            }
        }

        if (!flowInvocation.originalUrl) {
            flowInvocation.originalUrl = '';
        }
        flowInvocation.originalUrl = flowInvocation.originalUrl.replace("https://undefined/", config.PUBLIC_APP_URL).replace("https://https://", "https://");

        let originalRequest = flowInvocation.originalRequest2Preview || flowInvocation.originalRequest;
        if (originalRequest) {
            try {
                let origReq = JSON.parse(originalRequest);
                if (typeof origReq === 'string') {
                    originalRequest = origReq;
                } else {
                    originalRequest = JSON.stringify(origReq, null, 4);
                }
            } catch (e) {
                /* ignore error */
            }
        }

        return (
            <AppPanel breadcrumbParams={[
                {type, data: flowInvocation},
                {type: types['organisations'], data: this.props.user.organisation},
                {type: types['flows'], data: this.props.user.organisation.flow}
            ]} title={this.props.intl.formatMessage(messages.title)}>
				<h3><FormattedMessage defaultMessage="Flow Invocation" id="flow.flowInvocationDetail" /></h3>

				<div className="job-page">

                    <DetailBox data={flowInvocation} metadata={type} urlMapping={urlMapping}>
                        <DetailToolBar data={flowInvocation} metadata={type} urlMapping={urlMapping}>
                            {!flowInvocation.finished ? <RefreshButton autoRefresh={!flowInvocation.finished} fastRefresh relay={this.props.relay} /> : null}
                            <Link className={"btn btn-default"} key='fetches' to={urlMapping.getUrl(types['fetches'], 'list-flowInvocation', flowInvocation)}><i className="fa fa-fw fa-history"></i> <FormattedMessage defaultMessage="API Requests" id="fetches.list" /></Link>
                            {isDeveloper ?
                                (hasLogs ?
                                    <Link className={"btn btn-default"} key='logger' to={urlMapping.getUrl(types['flowInvocations'], 'logger', flowInvocation)}><i className="fa fa-fw fa-history"></i> <FormattedMessage defaultMessage="Logs" id="logger.list" /></Link>
                                    :
                                    <span className={"btn btn-default disabled"} key='logger'><i className="fa fa-fw fa-history"></i> <FormattedMessage defaultMessage="Logs" id="logger.list" /></span>
                                )
                                : undefined}
                            {isDeveloper ?
                                <LoadingButton className="btn btn-default" disabled={!hasLogs} icon="fa-download" key='download-log' onClick={() => { return this.downloadLogs('log'); }} text={<FormattedMessage {...messages.downloadLog} />} />
                                : undefined}
                            {isDeveloper ?
                                <LoadingButton className="btn btn-default" disabled={flowInvocation.tryCancel || flowInvocation.cancelled || flowInvocation.finished} icon="fa-remove" key='cancel' onClick={() => { this.onClickCancel(); }} text={<FormattedMessage defaultMessage="Stop" id="flowInvocation.cancel" />} />
                                : undefined}
                            {isDeveloper ?
                                <LoadingButton className="btn btn-default" disabled={flow.paused || !flow.enabled || flow.flowDef.globallyDisabled || this.props.user.organisation.blocked} icon="fa-refresh" key='retry' onClick={() => { this.onClickRetry(); }} text={<FormattedMessage defaultMessage="Retry" id="flowInvocation.retry" />} />
                                : undefined}

                            {canCaptureSnapshotTesting ? (
                                <SaveAsTestButton relay={this.props.relay} flowInvocation={flowInvocation} onError={this.onSaveAsTestError} />
                            ) : null}

                            {BrowserPermission.hasPermission(Permission.PERM_DEVELOPER) && BrowserPermission.developerModeEnabled()  ? <LoadingButton className="btn btn-default" onClick={() => this.onClickExtendExpiration()} icon="fa-download" text={<FormattedMessage defaultMessage="Keep API for 1 year" id="fetch.extendExpiration" />}/> : null}

                        </DetailToolBar>

                        {this.state.saveAsTestError ? (
                            <div className="alert alert-danger" role="alert" style={{ marginTop: 10, marginBottom: 8, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {this.state.saveAsTestError}
                            </div>
                        ) : null}

                        <div style={{"padding": '2em .5em'}}><FlowInvocationStatus flowInvocation={flowInvocation} /></div>

                        <TableDetail columns={{
                            'startType': {},
                            'createdAt': {},
                            'lastRun': {},
                            'finishedAt': {},
                            'error': {},
                            'author': {
                                render: (column, author) => {
                                    if (author) {
                                        return (<UserRenderer user={author} />);
                                    }

                                    return null;
                                },
                            },
                        }} data={flowInvocation} metadata={type} />

                        <TableDetail columns={{
                            'priority': {},
                            'cancel': {},
                            'tryCancel': {},
                            'debug': {},
                            'flowStepDef.name': {},
                            'nextRetry': {},
                            'attempt': {},
                        }} data={flowInvocation} metadata={type} />

                        {flowInvocation.startType === FLOW_STEP_HOOK && (BrowserPermission.hasPermission(Permission.PERM_INSTANCE_DEVELOPER) || BrowserPermission.hasPermission(Permission.PERM_DEVELOPER) && BrowserPermission.developerModeEnabled()) ?
                            <CopyToClipboard text={this.constructCURL(flowInvocation)}>
                                <div className="btn btn-default" style={{float: 'right'}}><span className="fa fa-clipboard" /> <FormattedMessage defaultMessage="Copy as CURL" id="flowInvocation.copyAsCurl" /></div>
                            </CopyToClipboard>
                        : null}

                        {BrowserPermission.hasPermission(Permission.PERM_DEVELOPER) && BrowserPermission.developerModeEnabled() ?
                            <div>
                                <TableDetail columns={{
                                    'flowDefVersion.createdAt': {
                                        render: () => {
                                            if (flowInvocation.flowDefVersion) {
                                                return <Link to={urlMapping.getUrl(types['flowDefVersions'], 'view', {flowDef_id: flowInvocation.flowDefVersion.flowDef_id, id: flowInvocation.flowDefVersion_id})}><FormattedDate value={flowInvocation.flowDefVersion.createdAt}/> ${flowInvocation.flowDefVersion.gitTag.substring(0, 3)}</Link>;
                                            }
                                            return <span style={{color: '#ff0000'}}>Dev version</span>;
                                        }
                                    },
                                    'productVersion': {},
                                    'productStage': {},
                                    'flowIncomingEmail.createdAt': {
                                        linkToView: true,
                                    },
                                    'resultStatusCode': {},
                                    'resultContentType': {},
                                    'originalUrl': {},
                                    'remoteIP': {},
                                }} data={flowInvocation} metadata={type} />
                                {originalHeaders ? <div className="form-control" readOnly style={{width: '100%', height: '20em', 'overflow': 'auto'}}>{originalHeaders}</div> : null}
                                {originalRequest ? <textarea className="form-control" readOnly rows="10" style={{width: '100%'}} value={originalRequest} /> : null}
                                {flowInvocation.resultResponse ? <textarea className="form-control" readOnly rows="10" style={{width: '100%'}} value={flowInvocation.resultResponse} /> : null}
                                {this.getStats(flowInvocation)}
                            </div> : null}
                    </DetailBox>
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FlowInvocationView), (props) => {
    return props.user.organisation && props.user.organisation.flow && props.user.organisation.flow.invocation;
}), {
    initialVariables: {
        organisationId: '',
        flowId: '',
        flowInvocationId: '',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    name,
                    blocked,
                    flow(id: $flowId) {
                        id,
                        organisation_id,
                        name,
                        paused,
                        enabled,
                        flowDef {
                            id,
                            name,
                            globallyDisabled,
                        },
                        invocation(id: $flowInvocationId) {
                            id,
                            realId,
                            startType,
                            stats,
                            organisation_id,
                            flow_id,
                            finished,
                            started,
                            debug,
                            finishedAt,
                            failed,
                            error,
                            tryCancel,
                            attempt,
                            cancelled,
                            nextRetry,
                            lastRun,
                            author {
                                id,
                                name,
                                firstname, 
                                surname, 
                                email, 
                                picture
                            },
                            flowDefVersion_id,
                            flowDefVersion {
                                id,
                                realId,
                                description,
                                flowDef_id,
                                gitTag,
                                createdAt,
                            },    
                            createdAt,
                            originalUrl,
                            remoteIP,
                            originalRequest,
                            originalRequest2Preview,
                            originalHeaders,
                            logger(first: 1) {
                                edges {
                                    node {
                                        id
                                    }
                                }
                            },
                            ${FlowInvocationStatus.getFragment("flowInvocation")}
                            resultStatusCode,
                            flowIncomingEmail {
                                id,
                                realId,
                                flow_id,
                                organisation_id,
                                emailSubject,
                                createdAt,
                            },
                            resultContentType,
                            resultResponse,
                            productVersion,
            				productStage,
                            priority,
                            flowStepDef {
                                id,
                                realId,
                                name,
                            }
                        }
                    }
                }
            }
        `,
    },
});
